import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe2, LogIn, UserCircle2 } from "lucide-react";
import { NAV_LINKS, LEGAL_LINKS, REGIONS } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const location = useLocation();
  const { region, link, setRegionPreference } = useRegion();
  const { isLoggedIn, email, revealAdminUi } = useAdminAuth();
  const { isLoggedIn: isCustomerLoggedIn } = useCustomerAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setLegalOpen(false);
    setRegionOpen(false);
  }, [location.pathname]);

  const isActive = (to) => {
    const full = link(to);
    return to === "/" ? location.pathname === full : location.pathname.startsWith(full);
  };

  // Switch region while keeping the same sub-page
  const switchTo = (target) => {
    let rest = location.pathname;
    if (region === "ph") rest = rest.replace(/^\/ph/, "") || "/";
    if (target === "ph") return rest === "/" ? "/ph" : `/ph${rest}`;
    return rest === "" ? "/" : rest;
  };

  const RegionSwitch = ({ mobile }) => (
    <div className={mobile ? "" : "relative"}>
      <button
        data-testid="region-switch-trigger"
        onClick={() => setRegionOpen((v) => !v)}
        aria-expanded={regionOpen}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-medium text-[#0F172A] hover:border-[#10B981] transition-colors"
      >
        <Globe2 size={15} className="text-[#10B981]" />
        {REGIONS[region].short}
        <ChevronDown size={14} />
      </button>
      {regionOpen && (
        <div className={mobile ? "mt-2" : "absolute right-0 top-full pt-2 w-52 z-50"}>
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-lg p-2">
            <Link
              to={switchTo("eu")}
              onClick={() => setRegionPreference("eu")}
              data-testid="region-switch-eu"
              className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                region === "eu" ? "bg-[#10B981]/10 text-[#10B981] font-semibold" : "text-[#475569] hover:bg-[#F8FAFC]"
              }`}
            >
              🌍 International / Europe (EUR)
            </Link>
            <Link
              to={switchTo("ph")}
              onClick={() => setRegionPreference("ph")}
              data-testid="region-switch-ph"
              className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                region === "ph" ? "bg-[#10B981]/10 text-[#10B981] font-semibold" : "text-[#475569] hover:bg-[#F8FAFC]"
              }`}
            >
              🇵🇭 Philippines
            </Link>
          </div>
        </div>
      )}
    </div>
  );

  const AccountButton = ({ mobile }) => (
    <Link
      to={revealAdminUi ? "/admin/dashboard" : isCustomerLoggedIn ? "/account/dashboard" : "/account/login"}
      data-testid="navbar-account-button"
      title={revealAdminUi ? `Admin: ${email}` : isCustomerLoggedIn ? "Customer account" : "Customer login"}
      className={
        mobile
          ? "flex items-center gap-2 px-3 py-3 rounded-lg text-base font-medium text-[#1E293B]"
          : "inline-flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-medium text-[#0F172A] hover:border-[#10B981] transition-colors"
      }
    >
      {revealAdminUi || isCustomerLoggedIn ? <UserCircle2 size={15} className="text-[#10B981]" /> : <LogIn size={15} className="text-[#10B981]" />}
      {revealAdminUi ? "Account" : isCustomerLoggedIn ? "My Account" : "Log in"}
    </Link>
  );

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-[#E2E8F0] shadow-sm" : "bg-white border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to={link("/")} data-testid="navbar-logo" className="flex items-center gap-2 shrink-0">
          <img src="/images/logo.png" alt="MySiteCare" className="h-11 w-auto object-contain" />
          {region === "ph" && (
            <span className="rounded-full bg-[#10B981]/10 text-[#10B981] text-[11px] font-bold px-2 py-0.5">PH</span>
          )}
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={link(l.to)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive(l.to) ? "text-[#10B981]" : "text-[#1E293B] hover:text-[#10B981]"
              }`}
            >
              {l.label}
            </Link>
          ))}

          <div className="relative" onMouseEnter={() => setLegalOpen(true)} onMouseLeave={() => setLegalOpen(false)}>
            <button
              data-testid="nav-legal-trigger"
              className="px-4 py-2 text-sm font-medium rounded-lg text-[#1E293B] hover:text-[#10B981] transition-colors flex items-center gap-1"
            >
              Legal <ChevronDown size={15} />
            </button>
            {legalOpen && (
              <div className="absolute right-0 top-full pt-2 w-60">
                <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-lg p-2">
                  {LEGAL_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={link(l.to)}
                      data-testid={`nav-legal-${l.to.replace("/", "")}`}
                      className="block px-3 py-2 text-sm text-[#475569] hover:text-[#10B981] hover:bg-[#F8FAFC] rounded-lg transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <RegionSwitch />
          <AccountButton />
          <Link
            to={link("/contact")}
            data-testid="navbar-cta"
            className="inline-flex items-center justify-center rounded-xl bg-[#10B981] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#059669] transition-colors shadow-sm"
          >
            Start Your Project
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#0F172A]"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E2E8F0] bg-white px-4 py-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={link(l.to)}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={`block px-3 py-3 rounded-lg text-base font-medium ${
                isActive(l.to) ? "text-[#10B981] bg-[#F8FAFC]" : "text-[#1E293B]"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-2 mt-2 border-t border-[#E2E8F0]">
            <p className="px-3 py-1 text-xs uppercase tracking-wide text-[#94A3B8]">Legal</p>
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={link(l.to)}
                data-testid={`mobile-nav-legal-${l.to.replace("/", "")}`}
                className="block px-3 py-2.5 rounded-lg text-sm text-[#475569]"
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="pt-2 mt-2 border-t border-[#E2E8F0]">
            <p className="px-3 py-1 text-xs uppercase tracking-wide text-[#94A3B8]">Region</p>
            <RegionSwitch mobile />
          </div>
          <div className="pt-2 mt-2 border-t border-[#E2E8F0]">
            <AccountButton mobile />
          </div>
          <Link
            to={link("/contact")}
            className="mt-3 block text-center rounded-xl bg-[#10B981] px-5 py-3 text-sm font-semibold text-white"
          >
            Start Your Project
          </Link>
        </div>
      )}
    </header>
  );
}
